import { Div, RefDate, RefBool, Ref, Collection, HandlersCollection, Radio, Checkbox, TableBuilder, Link, TextInput, Switch, NumberInput, RefString } from "@tblabs/truffle";
import { DateSlider } from "../Components/Utils/DateSlider";
import { BasketItem } from "../Models/Basket/BasketItem";
import { DeliveryForm } from "../Models/DeliveryForm";
import { PaymentForm } from "../Models/Payment/PaymentForm";
import { ReturnForm } from "../Models/ReturnForm";
import { Discount, RawDiscount, Ticket } from "../Models/Ticket";
import { SpecteamShopContentProvider } from "../Services/SpecteamShopContentProvider";
import { RefWatcher } from "./PlaygroundPage";
import { BasketItemBuilder } from "./BasketItemBuilder";
import { PaymentPresets } from "./PaymentPresets";
import { v4 as uuidv4 } from 'uuid';
import { DefaultButton, XPrimaryButton } from "../Components/Utils/XPrimaryButton";
import { Icons } from "../Components/Utils/Icons";
import { AdminBasketTableItem } from "./Admin/OrdersView/Views/Table/Components/BasketTableItem";
import { Table } from "@tblabs/truffle/Controls/TableBuilder/Table";
import { RentBasketItem } from "../Models/Basket/RentBasketItem";
import { CustomerExperienceSelector } from "./Admin/OrderEdit/Components/CustomerExperienceSelector";
import { BasketItemType } from "../Models/Basket/BasketItemType";
import { SaleBasketItem } from "../Models/Basket/SaleBasketItem";
import { DateTimeProvider } from "../Services/DateTimeProvider";
import { ProductOption } from "../Models/Basket/ProductOption";
import moment from "moment";
import { Box } from "../Components/Utils/Box";
import { RawProductInfo } from "../Models/Basket/Product";


export class TicketBuilder extends Div
{
    private orderId = new RefString(uuidv4()).Storable("ticket-builder:order-id");
    private createdAt = new RefDate().Storable("ticket-builder:created-at");
    private withTraining = new RefBool(false).Storable("ticket-builder:with-training");
    private withInvoice = new RefBool(false).Storable("ticket-builder:with-invoice");
    private depositPaymentForm = new Ref<PaymentForm.Cash | PaymentForm.BankTransfer | PaymentForm.Crypto | PaymentForm.Blik>(PaymentForm.Cash).Storable("ticket-builder:deposit-payment-form");
    private basketPaymentForm = new Ref<PaymentForm.Cash | PaymentForm.CashAtDelivery | PaymentForm.BankTransfer | PaymentForm.Crypto | PaymentForm.Blik>(PaymentForm.Cash).Storable("ticket-builder:basket-payment-form");
    private paymentPresets = new Ref(PaymentPresets.Unset).Storable("ticket-builder:payment-preset");
    private deliveryForm = new Ref<DeliveryForm>(DeliveryForm.Unset).Storable("ticket-builder:delivery-form");
    private returnForm = new Ref<ReturnForm>(ReturnForm.Unset).Storable("ticket-builder:return-form");
    private basketItems = new Collection<BasketItem>();
    private discounts = new Collection<Discount>();
    private customerName = new Ref<string>("").Storable("ticket-builder:customer-name");
    private customerEmail = new Ref<string>("").Storable("ticket-builder:customer-email");
    private customerPhone = new Ref<string>("").Storable("ticket-builder:customer-phone");
    private customerAddress = new Ref<string>("").Storable("ticket-builder:customer-address");
    private customerExperience = new Ref<number>(1).Storable("ticket-builder:customer-experience");
    private invoiceCompanyName = new Ref<string>("").Storable("ticket-builder:invoice-company-name");
    private invoiceCompanyAddress = new Ref<string>("").Storable("ticket-builder:invoice-company-address");
    private invoiceCompanyNIP = new Ref<string>("").Storable("ticket-builder:invoice-company-nip");

    public LoadTicket(ticket: Ticket): void
    {
        this.orderId.value = ticket.Id;
        this.createdAt.value = ticket.Meta.CreatedAt;
        this.withTraining.value = ticket.WithTraining;
        this.withInvoice.value = ticket.Invoice !== undefined;
        this.invoiceCompanyName.value = ticket.Invoice?.CompanyName || "";
        this.invoiceCompanyAddress.value = ticket.Invoice?.CompanyAddress || "";
        this.invoiceCompanyNIP.value = ticket.Invoice?.CompanyNIP || "";
        this.basketItems.Load(
            ...ticket.BasketList
                .map(item => new Switch<BasketItemType, BasketItem>(item.Type)
                    .Case(BasketItemType.Rent, () => new RentBasketItem(item))
                    .Case(BasketItemType.Sale, () => new SaleBasketItem(item))
                    .Execute()).filter(x => x !== undefined));
        this.deliveryForm.value = ticket.Delivery;
        this.returnForm.value = ticket.Return;
        this.customerName.value = ticket.Customer.Name;
        this.customerEmail.value = ticket.Customer.Email;
        this.customerPhone.value = ticket.Customer.Phone;
        this.customerAddress.value = ticket.Customer.Address;
        this.customerExperience.value = ticket.Customer.Experience || 0;
        this.depositPaymentForm.value = ticket.DepositPaymentForm as PaymentForm.Cash | PaymentForm.BankTransfer | PaymentForm.Crypto | PaymentForm.Blik;
        this.basketPaymentForm.value = ticket.BasketPaymentForm as PaymentForm.Cash | PaymentForm.CashAtDelivery | PaymentForm.BankTransfer | PaymentForm.Crypto | PaymentForm.Blik;
    }

    constructor(private _shop: SpecteamShopContentProvider)
    {
        super();
    }

    public OnChange = new HandlersCollection<(ticket: Ticket) => void>();

    public Build(): void
    {
        const ticket: Ticket = {
            Id: this.orderId.value,
            BasketList: this.basketItems.Items.map(item => item.AsRaw),
            BasketInfo: {
                PriceBeforeDiscount: this.basketItems.Items
                    .reduce((acc, item) => acc + item.TotalPrice.value, 0),
                PriceAfterDiscount: (-1),
                Discounts: this.discounts.Items.map(item => item.AsRaw),
            },
            DepositPaymentForm: this.depositPaymentForm.value,
            BasketPaymentForm: this.basketPaymentForm.value,
            Delivery: this.deliveryForm.value,
            Return: this.returnForm.value,
            Customer: {
                Name: this.customerName.value,
                Email: this.customerEmail.value,
                Phone: this.customerPhone.value,
                Address: this.customerAddress.value,
                Id: uuidv4(),
                Type: "Klient",
                Experience: this.customerExperience.value,
            },
            Meta: {
                CreatedAt: new Date(this.createdAt.value),
                CreatedBy: "admin",
            },
            WithTraining: this.withTraining.value,
            PickupPlace: "PickupPlace1",
            Invoice: this.withInvoice.value ? {
                CompanyName: this.invoiceCompanyName.value,
                CompanyAddress: this.invoiceCompanyAddress.value,
                CompanyNIP: this.invoiceCompanyNIP.value,
            } : undefined,
        } as Ticket;

        this.OnChange.Call(ticket);
    }

    async OnAppend()
    {
        this.paymentPresets.OnChange(preset =>
        {
            switch (preset)
            {
                case PaymentPresets.AllWithCash:
                    this.depositPaymentForm.value = PaymentForm.Cash;
                    this.basketPaymentForm.value = PaymentForm.Cash;
                    break;
                case PaymentPresets.AllWithBankTransfer:
                    this.depositPaymentForm.value = PaymentForm.BankTransfer;
                    this.basketPaymentForm.value = PaymentForm.BankTransfer;
                    break;
                case PaymentPresets.AllWithCrypto:
                    this.depositPaymentForm.value = PaymentForm.Crypto;
                    this.basketPaymentForm.value = PaymentForm.Crypto;
                    break;
                case PaymentPresets.DepositWithBankTransfer_ServiceWithCash:
                    this.depositPaymentForm.value = PaymentForm.BankTransfer;
                    this.basketPaymentForm.value = PaymentForm.CashAtDelivery;
                    break;
                case PaymentPresets.DepositWithBlik_ServiceWithCash:
                    this.depositPaymentForm.value = PaymentForm.Blik;
                    this.basketPaymentForm.value = PaymentForm.CashAtDelivery;
                    break;
            }
        }, true);

        const paymentRadio = new Radio(this.paymentPresets)
            .Add(PaymentPresets.AllWithCash, "Całość płatna gotówką przy odbiorze")
            .Add(PaymentPresets.DepositWithBankTransfer_ServiceWithCash, "Kaucja płatna przelewem, reszta gotówką przy odbiorze")
            .Add(PaymentPresets.AllWithBankTransfer, "Całość z góry przelewem")
            .Add(PaymentPresets.AllWithCrypto, "Całość Bitcoinem z góry")
            .Add(PaymentPresets.DepositWithBlik_ServiceWithCash, "Kaucja płatna Blikem, reszta gotówką przy odbiorze");

        const deliveryRadio = new Radio(this.deliveryForm)
            .Add(DeliveryForm.Personal, "Odbiór osobisty")
            .Add(DeliveryForm.Package, "Dostawa kurierem");

        const returnRadio = new Radio(this.returnForm)
            .Add(ReturnForm.Personal, "Zwrot osobisty")
            .Add(ReturnForm.Package, "Zwrot paczkomatem");

        RefWatcher.Watch([
            this.basketItems, this.paymentPresets, this.deliveryForm, this.returnForm, this.withTraining,
            this.customerName, this.customerEmail, this.customerPhone, this.customerAddress, this.customerExperience,
            this.withInvoice, this.invoiceCompanyName, this.invoiceCompanyAddress, this.invoiceCompanyNIP,
        ], () => this.Build());

        const showAddItem = new RefBool(false);
        const addBtn = new XPrimaryButton("Dodaj", Icons.Plus, (btn) => { showAddItem.True(); btn.Hide(); });
        const addSampleBtn = new DefaultButton("Dodaj K1M1 na 3 dni", Icons.Plus, async () =>
        {
            const shop = await this._shop.Get();

            const initialRentItem = new RentBasketItem({
                ProductInfo: { Id: shop.Products[0].Id, Name: shop.Products[0].BasketName, Picture: shop.Products[0].Images[0].MiniUrl } as RawProductInfo,
                Option: { Id: shop.Products[0].Options.List[0].Url } as ProductOption,
                Quantity: 1,
                TotalPrice: 500,
                Deposit: 300,
                From: moment(DateTimeProvider.Now).add(3, "day").toDate(),
                To: moment(DateTimeProvider.Now).add(5, "day").toDate(),
            });

            this.basketItems.Add(initialRentItem);
        });
        const basketItemBuilder = new BasketItemBuilder(this._shop, (item) =>
        {
            if (item)
                this.basketItems.Add(item);
            showAddItem.False();
            addBtn.Show();
        })
            .Visible(showAddItem);

        this.Clear().Append(
            new Table([], [
                ["Id", new TextInput(this.orderId)],
                ["Data utworzenia:", new DateSlider(this.createdAt)],
                ["Produkty", new Box(
                    new TableBuilder(this.basketItems)
                        .AddColumn("", item => new AdminBasketTableItem(item))
                        .AddColumn("", item => new Link("✖️").OnClick(() => this.basketItems.Remove(item)))
                        .Build(),
                    addBtn,
                    addSampleBtn,
                    basketItemBuilder)],
                ["Rabaty", new Box(
                    new TableBuilder(this.discounts)
                        .AddColumn("Nazwa", item => new TextInput(item.Name))
                        .AddColumn("Kod", item => new TextInput(item.Code))
                        .AddColumn("Wartość", item => new NumberInput(item.Value))
                        .AddColumn("Opcje", item => new Link("✖️").OnClick(() => this.discounts.Remove(item)))
                        .Build(),
                    new DefaultButton("Dodaj rabat", Icons.Plus, () => this.discounts.Add(new Discount({}))))],
                ["Płatność:", paymentRadio],
                ["Dostawa:", deliveryRadio],
                ["Zwrot:", returnRadio],
                ["Trening:", new Checkbox(this.withTraining)],
                ["Klient:",
                    new Table([], [
                        ["Imię", new TextInput(this.customerName)],
                        ["Email", new TextInput(this.customerEmail)],
                        ["Telefon", new TextInput(this.customerPhone)],
                        ["Adres", new TextInput(this.customerAddress)],
                        ["Doświadczenie", new CustomerExperienceSelector(this.customerExperience)],
                    ]).Class("uk-table uk-table-small uk-table-striped"),
                ],
                ["Faktura:", new Table([], [
                    ["Wystawiać fakturę?", new Checkbox(this.withInvoice)],
                    ["Nazwa firmy", new TextInput(this.invoiceCompanyName)],
                    ["Adres firmy", new TextInput(this.invoiceCompanyAddress)],
                    ["NIP firmy", new TextInput(this.invoiceCompanyNIP)],
                ]).Class("uk-table uk-table-small uk-table-striped")]
            ]).Class("uk-table uk-table-small uk-table-striped"),
        );
    }
}
